import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { ShoppingCart, Plus, Search, CheckCircle, Clock, Truck, AlertCircle, Eye, Edit } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

function PurchaseOrder() {
  const { API, user } = useContext(AppContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState('list');

  const [newPO, setNewPO] = useState({
    supplier: '',
    material: '',
    quantity: '',
    unitPrice: '',
    deliveryDate: '',
    remarks: ''
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/stores/purchase-orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'Partial': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Ordered': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Cancelled': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed': return <CheckCircle size={14} />;
      case 'Partial': return <Truck size={14} />;
      case 'Ordered': return <Clock size={14} />;
      default: return null;
    }
  };

  // Chart data
  const chartData = orders.map(o => ({
    name: o.id.replace('PO-2025-', 'PO-'),
    Ordered: o.orderedQty,
    Delivered: o.deliveredQty,
  }));

  const handleSubmitPO = async () => {
    const qty = parseInt(newPO.quantity);
    const price = parseFloat(newPO.unitPrice);
    try {
      const response = await axios.post(`${API}/stores/purchase-orders`, {
        supplier: newPO.supplier,
        items: [{ material: newPO.material, quantity: qty, unitPrice: price }],
        totalAmount: qty * price,
        deliveryDate: newPO.deliveryDate,
        paymentTerms: 'Net 30'
      });
      fetchOrders();
      setShowAddModal(false);
      setNewPO({ supplier: '', material: '', quantity: '', unitPrice: '', deliveryDate: '', remarks: '' });
    } catch (error) {
      console.error('Error creating PO:', error);
    }
  };

  const stats = {
    total: orders.length,
    completed: orders.filter(o => o.status === 'Completed').length,
    partial: orders.filter(o => o.status === 'Partial').length,
    ordered: orders.filter(o => o.status === 'Ordered').length,
    totalValue: orders.reduce((sum, o) => sum + o.totalValue, 0),
  };

  if (loading) return <div className="text-center py-12">Loading purchase orders...</div>;

  return (
    <div className="space-y-6" data-testid="purchase-order-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchase Orders</h1>
          <p className="text-gray-500 mt-1">Track and manage procurement orders</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md">
          <Plus size={18} /><span>New PO</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200"><p className="text-sm text-gray-500">Total POs</p><p className="text-2xl font-bold text-gray-900">{stats.total}</p></div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center space-x-2"><CheckCircle className="text-green-500" size={18} /><p className="text-sm text-green-600 font-medium">Completed</p></div>
          <p className="text-2xl font-bold text-green-700">{stats.completed}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
          <div className="flex items-center space-x-2"><Truck className="text-blue-500" size={18} /><p className="text-sm text-blue-600 font-medium">Partial</p></div>
          <p className="text-2xl font-bold text-blue-700">{stats.partial}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
          <div className="flex items-center space-x-2"><Clock className="text-orange-500" size={18} /><p className="text-sm text-orange-600 font-medium">Ordered</p></div>
          <p className="text-2xl font-bold text-orange-700">{stats.ordered}</p>
        </div>
        <div className="bg-gradient-to-br from-[#1a237e]/5 to-[#283593]/10 rounded-xl p-5 border border-[#1a237e]/20">
          <p className="text-sm text-[#1a237e] font-medium">Total Value</p>
          <p className="text-2xl font-bold text-[#1a237e]">₹{(stats.totalValue / 100000).toFixed(1)}L</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center space-x-2">
        <button onClick={() => setActiveTab('list')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'list' ? 'bg-[#1a237e] text-white' : 'bg-gray-100 text-gray-600'}`}>PO List</button>
        <button onClick={() => setActiveTab('tracking')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'tracking' ? 'bg-[#1a237e] text-white' : 'bg-gray-100 text-gray-600'}`}>Delivery Tracking</button>
      </div>

      {activeTab === 'tracking' && (
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Ordered vs Delivered</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }} />
              <Legend />
              <Bar dataKey="Ordered" fill="#1a237e" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Delivered" fill="#2e7d32" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {activeTab === 'list' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
                <tr>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">PO Number</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Supplier</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Material</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Ordered</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Delivered</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Pending</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Value</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Created By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.map((po) => (
                  <tr key={po.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 font-mono text-sm text-[#1a237e] font-medium">{po.id}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">{po.supplier}</td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{po.material}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{po.orderedQty.toLocaleString()} {po.unit}</td>
                    <td className="px-4 py-4 text-sm font-semibold text-green-600">{po.deliveredQty.toLocaleString()}</td>
                    <td className="px-4 py-4 text-sm font-semibold text-orange-600">{po.pendingQty.toLocaleString()}</td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">₹{(po.totalValue / 100000).toFixed(2)}L</td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(po.status)}`}>
                        {getStatusIcon(po.status)}<span>{po.status}</span>
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${po.createdBy === 'Admin' ? 'bg-purple-100 text-purple-700' : po.createdBy === 'Store Incharge' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                        {po.createdBy}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add PO Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Create Purchase Order</h2>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Supplier *</label>
                <input type="text" value={newPO.supplier} onChange={(e) => setNewPO({...newPO, supplier: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" placeholder="Supplier name" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Material *</label>
                <input type="text" value={newPO.material} onChange={(e) => setNewPO({...newPO, material: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" placeholder="Material name" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                  <input type="number" value={newPO.quantity} onChange={(e) => setNewPO({...newPO, quantity: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" placeholder="0" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Unit Price (₹) *</label>
                  <input type="number" value={newPO.unitPrice} onChange={(e) => setNewPO({...newPO, unitPrice: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" placeholder="0" /></div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Expected Delivery Date *</label>
                <input type="date" value={newPO.deliveryDate} onChange={(e) => setNewPO({...newPO, deliveryDate: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" /></div>
              {newPO.quantity && newPO.unitPrice && (
                <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                  <p className="text-sm text-green-700">Total Order Value: <span className="font-bold">₹{(parseInt(newPO.quantity) * parseFloat(newPO.unitPrice)).toLocaleString()}</span></p>
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium">Cancel</button>
              <button onClick={handleSubmitPO} disabled={!newPO.supplier || !newPO.material || !newPO.quantity || !newPO.unitPrice || !newPO.deliveryDate}
                className="px-4 py-2.5 bg-gradient-to-r from-[#1a237e] to-[#283593] text-white rounded-xl font-medium disabled:opacity-50">Create PO</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PurchaseOrder;
